import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Cat, Scan, MessageSquare, BarChart3, Gift, CreditCard, Settings, ChevronRight, Sparkles, ArrowUpRight, AlertTriangle, Eye, Lightbulb, X } from 'lucide-react';
import useAuthStore from '../store/authStore';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';

const gradeConfig = {
  A: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Excellent' },
  B: { bg: 'bg-green-100', text: 'text-green-700', label: 'Good' },
  C: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Fair' },
  D: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Poor' },
  F: { bg: 'bg-red-100', text: 'text-red-700', label: 'Bad' },
};

const getStatusColor = (color) => ({
  GREEN: 'bg-[#BFDB38] text-[#1B4332]',
  YELLOW: 'bg-yellow-400 text-[#1B4332]',
  RED: 'bg-[#FF6B6B] text-white'
})[color?.toUpperCase()] || 'bg-gray-400 text-white';

const getStatusBorder = (color) => ({
  GREEN: 'border-[#BFDB38]',
  YELLOW: 'border-yellow-400',
  RED: 'border-[#FF6B6B]'
})[color?.toUpperCase()] || 'border-gray-200';

const getActionBadge = (action) => ({
  SAFE: 'bg-[#BFDB38] text-[#1B4332]',
  CAUTION: 'bg-yellow-400 text-[#1B4332]',
  WARNING: 'bg-[#FF6B6B] text-white'
})[action?.toUpperCase()] || 'bg-gray-400 text-white';

const getActionBorder = (action) => ({
  SAFE: 'border-[#BFDB38] bg-[#BFDB38]/5',
  CAUTION: 'border-yellow-400 bg-yellow-50',
  WARNING: 'border-[#FF6B6B] bg-[#FF6B6B]/5'
})[action?.toUpperCase()] || 'border-gray-200 bg-gray-50';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [catProfiles, setCatProfiles] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [dashMeta, setDashMeta] = useState(null);
  const [sampleReport, setSampleReport] = useState(null);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    loadData();
    checkFeedback();
  }, []);

  const checkFeedback = async () => {
    try {
      const res = await api.get('/check-pending-feedback');
      if (res.data.has_pending) {
        setPendingFeedback(res.data);
        setShowFeedbackModal(true);
      }
    } catch { /* silent */ }
  };

  const handleFeedbackSubmit = async (reaction) => {
    if (!pendingFeedback) return;
    try {
      await api.post('/scan-feedback', { scan_id: pendingFeedback.scan_id, reaction });
      toast.success("Thanks! We've saved their reaction 😸");
      setShowFeedbackModal(false);
      setPendingFeedback(null);
    } catch {
      toast.error('Could not save feedback');
    }
  };

  const loadData = async () => {
    try {
      const [profilesRes, scansRes, subRes, metaRes] = await Promise.all([
        api.get('/cat-profiles'),
        api.get('/ingredient-scans'),
        api.get('/subscription'),
        api.get('/dashboard-meta')
      ]);
      setCatProfiles(profilesRes.data);
      setRecentScans(scansRes.data.slice(0, 5));
      setSubscription(subRes.data);
      setDashMeta(metaRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadSampleReport = async () => {
    try {
      const res = await api.get('/sample-scan');
      setSampleReport(res.data);
      setShowSampleReport(true);
    } catch {
      toast.error('Failed to load sample report');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-[3px] border-[#1B4332] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const isFirstTime = dashMeta?.greeting_type === 'first_time';
  const progressPercent = dashMeta?.setup_progress ?? 0;

  return (
    <DashboardLayout>
      {/* Dynamic Greeting */}
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-1">
          {isFirstTime ? 'Welcome to PurrCheck' : 'Welcome back'}
        </p>
        <h1 className="text-xl sm:text-2xl font-extrabold text-[#1B4332] tracking-tight" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="dashboard-welcome">
          {user?.full_name}
        </h1>
        {dashMeta?.greeting && (
          <p className="text-[13px] text-[#1B4332]/60 mt-1.5 font-medium leading-relaxed max-w-lg" data-testid="dashboard-greeting">
            {dashMeta.greeting}
          </p>
        )}
      </div>

      {/* Gamified Progress Bar */}
      {dashMeta && progressPercent < 100 && (
        <div className="mb-6 bg-white border border-[#1B4332]/8 rounded-xl p-4" data-testid="progress-tracker">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Setup Progress</p>
            <span className="text-[11px] font-bold text-[#1B4332]/50">{progressPercent}%</span>
          </div>
          <div className="w-full h-2.5 bg-[#1B4332]/5 rounded-full overflow-hidden mb-2.5">
            <div
              className="h-full bg-gradient-to-r from-[#BFDB38] to-[#2D6A4F] rounded-full transition-all duration-700 ease-out"
              style={{ width: `${Math.max(progressPercent, 5)}%` }}
            />
          </div>
          <p className="text-[13px] font-semibold text-[#1B4332]/70" data-testid="progress-step">
            {dashMeta.progress_step}
          </p>
        </div>
      )}

      {/* Completed Progress Badge */}
      {dashMeta && progressPercent === 100 && (
        <div className="mb-6 bg-gradient-to-r from-[#1B4332] to-[#2D6A4F] rounded-xl px-4 py-3 flex items-center gap-3" data-testid="progress-complete">
          <div className="w-8 h-8 bg-[#BFDB38] rounded-full flex items-center justify-center shrink-0">
            <span className="text-[#1B4332] text-sm font-black">✓</span>
          </div>
          <p className="text-[13px] font-semibold text-white/90">{dashMeta.progress_step}</p>
        </div>
      )}

      {/* Subscription Banner */}
      {subscription && (
        <div className={`mb-6 rounded-xl px-4 py-3 border ${subscription.plan === 'premium' ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white border-[#1B4332]/10'}`} data-testid="subscription-status">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div>
              <p className={`text-[10px] uppercase tracking-widest font-semibold mb-0.5 ${subscription.plan === 'premium' ? 'text-[#BFDB38]' : 'text-[#1B4332]/40'}`}>Current Plan</p>
              <p className={`text-lg font-extrabold capitalize ${subscription.plan === 'premium' ? 'text-white' : 'text-[#1B4332]'}`} style={{ fontFamily: 'Nunito, sans-serif' }}>{subscription.plan}</p>
              <p className={`text-[12px] mt-0.5 ${subscription.plan === 'premium' ? 'text-white/60' : 'text-[#1B4332]/50'}`}>
                Scans: {subscription.scan_count}/{subscription.scan_limit || '∞'} · Chats: {subscription.chat_count}/{subscription.chat_limit || '∞'}
              </p>
            </div>
            {subscription.plan === 'free' && (
              <Button onClick={() => navigate('/subscription')} className="rounded-lg font-semibold bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-[12px] h-8 px-3 transition-all duration-200" data-testid="upgrade-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Upgrade
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Quick Nav Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { to: '/cat-profiles', icon: Cat, label: 'Cat Profiles', value: catProfiles.length, color: 'bg-emerald-50', iconColor: 'text-emerald-600' },
          { to: '/ingredient-checker', icon: Scan, label: 'Scan Food', value: isFirstTime ? '📸 Start' : 'Quick Scan', color: 'bg-orange-50', iconColor: 'text-orange-500' },
          { to: '/behavior-chat', icon: MessageSquare, label: 'Behavior Chat', value: 'Ask AI', color: 'bg-blue-50', iconColor: 'text-blue-500' },
          { to: '/analytics', icon: BarChart3, label: 'Analytics', value: 'Health Score', color: 'bg-violet-50', iconColor: 'text-violet-500' }
        ].map((card) => (
          <Link key={card.to} to={card.to}>
            <div className={`bg-white border border-[#1B4332]/8 rounded-xl p-3.5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer group ${isFirstTime && card.to === '/ingredient-checker' ? 'ring-2 ring-[#BFDB38] ring-offset-2' : ''}`} data-testid={`nav-card-${card.label.toLowerCase().replace(' ', '-')}`}>
              <div className={`w-9 h-9 ${card.color} rounded-lg flex items-center justify-center mb-2.5`}>
                <card.icon className={`w-4 h-4 ${card.iconColor}`} strokeWidth={2} />
              </div>
              <p className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-0.5">{card.label}</p>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-[#1B4332]">{card.value}</p>
                <ArrowUpRight className="w-3.5 h-3.5 text-[#1B4332]/20 group-hover:text-[#1B4332] transition-colors" strokeWidth={2} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Bottom Grid */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Recent Scans / Sample Report CTA */}
        <div className="bg-white border border-[#1B4332]/8 rounded-xl p-4" data-testid="recent-scans-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Recent Scans</h3>
            {recentScans.length > 0 && (
              <Link to="/ingredient-scans" className="text-[11px] font-semibold text-[#1B4332]/30 hover:text-[#1B4332] transition-colors">View all</Link>
            )}
          </div>
          {recentScans.length === 0 ? (
            <div className="text-center py-6" data-testid="empty-scans-state">
              <Scan className="w-10 h-10 text-[#1B4332]/10 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-[13px] text-[#1B4332]/40 mb-1 font-medium">No scans yet</p>
              <p className="text-[11px] text-[#1B4332]/30 mb-4 max-w-[220px] mx-auto">See what PurrCheck can do — view a sample report below, or scan your first food label.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <Button onClick={() => navigate('/ingredient-checker')} className="rounded-lg font-semibold bg-[#1B4332] hover:bg-[#2D6A4F] text-[12px] h-8 px-4 transition-all duration-200" data-testid="scan-first-food-btn">
                  <Scan className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Scan First Food
                </Button>
                <Button onClick={loadSampleReport} variant="outline" className="rounded-lg font-semibold border-2 border-[#1B4332]/15 hover:border-[#BFDB38] text-[#1B4332] text-[12px] h-8 px-4 transition-all duration-200" data-testid="view-sample-btn">
                  <Eye className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> View Sample Report
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {recentScans.map((scan) => {
                const grade = gradeConfig[scan.purrcheck_grade || 'C'] || gradeConfig.C;
                return (
                  <div key={scan.id} className="flex justify-between items-center px-3 py-2 rounded-lg hover:bg-[#1B4332]/3 transition-colors" data-testid={`scan-item-${scan.id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#1B4332] text-[13px] truncate">{scan.food_name || 'Food Scan'}</p>
                      <p className="text-[11px] text-[#1B4332]/40 truncate">{scan.brand || 'Unknown Brand'}</p>
                    </div>
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0 ${grade.bg} ${grade.text}`}>
                      {scan.purrcheck_grade || 'C'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Column: Quick Tip + Quick Actions */}
        <div className="space-y-4">
          {/* Curiosity Hook — Quick Tip */}
          {dashMeta?.quick_tip && (
            <div className="bg-gradient-to-br from-[#BFDB38]/10 to-[#1B4332]/5 border border-[#BFDB38]/25 rounded-xl p-4" data-testid="curiosity-hook">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-[#BFDB38]/20 rounded-lg flex items-center justify-center shrink-0">
                  <Lightbulb className="w-4.5 h-4.5 text-[#1B4332]" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-1">Quick Tip {dashMeta.quick_tip.emoji}</p>
                  <p className="text-[13px] text-[#1B4332]/80 font-medium leading-relaxed">{dashMeta.quick_tip.text}</p>
                  <button
                    onClick={() => navigate('/ingredient-checker')}
                    className="mt-2.5 text-[12px] font-bold text-[#1B4332] hover:text-[#2D6A4F] transition-colors inline-flex items-center gap-1"
                    data-testid="quick-tip-cta"
                  >
                    {dashMeta.quick_tip.cta} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white border border-[#1B4332]/8 rounded-xl p-4" data-testid="quick-links-section">
            <h3 className="text-[11px] uppercase tracking-widest font-semibold text-[#1B4332]/40 mb-3">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { to: '/affiliates', icon: Gift, label: 'Recommended Products', desc: 'Top-rated cat foods', accent: 'border-l-emerald-400' },
                { to: '/subscription', icon: CreditCard, label: 'Manage Subscription', desc: 'Plan & billing', accent: 'border-l-orange-400' },
                { to: '/settings', icon: Settings, label: 'Account Settings', desc: 'Profile & preferences', accent: 'border-l-blue-400' }
              ].map((link) => (
                <Link key={link.to} to={link.to}>
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#1B4332]/3 transition-all duration-200 cursor-pointer group border-l-[3px] ${link.accent}`} data-testid={`quick-link-${link.label.toLowerCase().replace(' ', '-')}`}>
                    <link.icon className="w-4 h-4 text-[#1B4332]/40 group-hover:text-[#1B4332] transition-colors shrink-0" strokeWidth={2} />
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-[#1B4332] text-[13px]">{link.label}</span>
                      <p className="text-[11px] text-[#1B4332]/35">{link.desc}</p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-[#1B4332]/20 group-hover:text-[#1B4332] transition-colors shrink-0" strokeWidth={2} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sample Report Modal Overlay */}
      {showSampleReport && sampleReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowSampleReport(false)} data-testid="sample-report-overlay">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#1B4332]/8 px-5 py-3.5 rounded-t-2xl flex items-center justify-between z-10">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#BFDB38]">Sample Report</p>
                <p className="text-sm font-bold text-[#1B4332]">{sampleReport.food_name}</p>
              </div>
              <button onClick={() => setShowSampleReport(false)} className="w-8 h-8 rounded-full hover:bg-[#1B4332]/5 flex items-center justify-center transition-colors" data-testid="close-sample-btn">
                <X className="w-4 h-4 text-[#1B4332]/50" strokeWidth={2} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Verdict */}
              <div className={`text-center p-5 border-2 rounded-2xl ${getStatusBorder(sampleReport.ingredients_analyzed?.verdict?.status_color)}`}>
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B] mb-2.5">OVERALL VERDICT</p>
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl text-4xl font-black ${getStatusColor(sampleReport.ingredients_analyzed?.verdict?.status_color)}`}>
                  {sampleReport.ingredients_analyzed?.verdict?.grade}
                </div>
                <h3 className="text-lg font-bold text-[#1B4332] mt-3 leading-tight">{sampleReport.ingredients_analyzed?.verdict?.headline}</h3>
                <p className="text-sm font-semibold text-[#1B4332]/60 mt-1">{sampleReport.food_name} by {sampleReport.brand}</p>
                {sampleReport.ingredients_analyzed?.verdict?.primary_issue && (
                  <p className="text-sm text-[#1B4332]/80 mt-2.5 bg-[#1B4332]/5 p-2 rounded-lg font-medium">
                    {sampleReport.ingredients_analyzed.verdict.primary_issue}
                  </p>
                )}
              </div>

              {/* Nutritionist Note (personalized_insight) */}
              {sampleReport.ingredients_analyzed?.personalized_insight && (
                <div className="p-4 bg-gradient-to-br from-[#BFDB38]/10 to-transparent border border-[#BFDB38]/30 rounded-xl flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#BFDB38] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60 mb-1">Nutritionist Note</p>
                    <p className="text-sm text-[#1B4332]/90 leading-relaxed font-medium">{sampleReport.ingredients_analyzed.personalized_insight}</p>
                  </div>
                </div>
              )}

              {/* Cost of Inaction */}
              {sampleReport.ingredients_analyzed?.cost_of_inaction && (
                <div className="p-4 bg-red-50 border-l-4 border-l-[#FF6B6B] border-t border-r border-b border-[#FF6B6B]/20 rounded-r-xl">
                  <div className="flex items-center gap-2 mb-1.5">
                    <AlertTriangle className="w-4 h-4 text-[#FF6B6B]" />
                    <p className="text-xs uppercase tracking-widest font-bold text-[#FF6B6B]">Cost of Inaction</p>
                  </div>
                  <p className="text-sm text-[#1B4332]/90 font-semibold">{sampleReport.ingredients_analyzed.cost_of_inaction}</p>
                </div>
              )}

              {/* Impact Analysis */}
              {sampleReport.ingredients_analyzed?.impact_analysis?.length > 0 && (
                <div className="space-y-2.5">
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1B4332]/60 px-1">Ingredient Breakdown</p>
                  {sampleReport.ingredients_analyzed.impact_analysis.map((item, i) => (
                    <div key={i} className={`p-3.5 rounded-xl border ${getActionBorder(item.action_type)}`}>
                      <div className="flex flex-wrap justify-between items-start gap-2 mb-1.5">
                        <div>
                          <p className="font-extrabold text-[#1B4332] text-sm">{item.ingredient}</p>
                          <p className="text-xs font-semibold text-[#1B4332]/50">{item.short_label}</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${getActionBadge(item.action_type)}`}>
                          {item.action_type}
                        </span>
                      </div>
                      <p className="text-[13px] text-[#1B4332]/80 leading-snug">{item.consequence}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Action Plan */}
              {sampleReport.ingredients_analyzed?.decision_action && (
                <div className="bg-[#1B4332] text-white p-4 rounded-xl">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#BFDB38] mb-2.5">Action Plan</p>
                  <p className="text-sm font-semibold mb-2 flex items-start gap-2">
                    <span className="text-[#BFDB38] mt-0.5">↳</span> {sampleReport.ingredients_analyzed.decision_action.immediate_step}
                  </p>
                  <p className="text-sm font-semibold flex items-start gap-2">
                    <span className="text-[#BFDB38] mt-0.5">↳</span> {sampleReport.ingredients_analyzed.decision_action.recommendation}
                  </p>
                </div>
              )}

              {/* CTA: Scan Your Own */}
              <div className="bg-gradient-to-r from-[#1B4332] to-[#2D6A4F] rounded-xl p-4 text-center">
                <p className="text-[11px] uppercase tracking-widest font-bold text-[#BFDB38] mb-1.5">This was a sample</p>
                <p className="text-sm text-white/80 font-medium mb-3">Now scan YOUR cat's food to get a personalized safety report.</p>
                <Button
                  onClick={() => { setShowSampleReport(false); navigate('/ingredient-checker'); }}
                  className="rounded-full font-bold bg-[#BFDB38] hover:bg-[#BFDB38]/90 text-[#1B4332] text-[13px] h-9 px-6 transition-all duration-200"
                  data-testid="scan-your-food-cta"
                >
                  <Scan className="w-4 h-4 mr-2" strokeWidth={2.5} /> Scan Your Food Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Feedback Modal */}
      {showFeedbackModal && pendingFeedback && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in duration-300">
            <div className="bg-gradient-to-r from-[#1B4332] to-[#2D6A4F] px-5 py-4 text-center">
              <p className="text-[11px] uppercase tracking-widest font-bold text-[#BFDB38] mb-1">Feedback Time!</p>
              <h3 className="text-xl font-bold text-white">{pendingFeedback.cat_name}'s Reaction?</h3>
            </div>
            <div className="p-5 text-center">
              <p className="text-sm font-semibold text-[#1B4332]/80 mb-5">
                How did {pendingFeedback.cat_name} react to <strong>{pendingFeedback.food_name}</strong>?
              </p>
              <div className="flex flex-col gap-2.5">
                <button onClick={() => handleFeedbackSubmit('loved_it')} className="w-full py-3 bg-emerald-50 border-2 border-emerald-200 rounded-xl text-[14px] font-bold text-emerald-700 hover:bg-emerald-100 transition-colors">😻 Loved it</button>
                <button onClick={() => handleFeedbackSubmit('neutral')} className="w-full py-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-[14px] font-bold text-amber-700 hover:bg-amber-100 transition-colors">🤔 Neutral / Okay</button>
                <button onClick={() => handleFeedbackSubmit('rejected')} className="w-full py-3 bg-red-50 border-2 border-red-200 rounded-xl text-[14px] font-bold text-red-700 hover:bg-red-100 transition-colors">😾 Rejected</button>
              </div>
              <button onClick={() => setShowFeedbackModal(false)} className="mt-4 text-xs font-semibold text-[#1B4332]/40 hover:text-[#1B4332]">Skip for now</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
